import { useCallback } from 'react';
import useMapParams from './useMapParams';
import useAnalyticsState from '../pages/utils/analyticsStateHooks';
import {
  parsePartyNameParam,
  setPartyNameParam,
} from '../utils/mapPartyParams';
import { resolveRequestPartyFilter } from '../utils/resolveRequestPartyFilter';

/**
 * Map importer/exporter: URL presence > selections.map > All.
 * Writes user edits to URL + analytics selections.map together.
 * Does not persist ACL or party payloads. Shared-link URL does not overwrite saved.
 *
 * Saved names apply only when the name is in today's options.
 */
const useMapPartyParams = ({
  importerOptions = [],
  exporterOptions = [],
  shipmentsView = false,
} = {}) => {
  const [searchParams, setSearchParams] = useMapParams();
  const { state, isFetched, updateSelections } = useAnalyticsState();

  const urlImporter = parsePartyNameParam(searchParams, 'importer');
  const urlExporter = parsePartyNameParam(searchParams, 'exporter');
  const savedMap = (state?.selections && state.selections.map) || {};
  const savedImporter = typeof savedMap.importer === 'string' ? savedMap.importer : '';
  const savedExporter = typeof savedMap.exporter === 'string' ? savedMap.exporter : '';

  const importer = resolveRequestPartyFilter({
    urlField: urlImporter,
    savedValue: savedImporter,
    options: importerOptions,
    isFetched,
  });

  const exporter = shipmentsView
    ? resolveRequestPartyFilter({
        urlField: urlExporter,
        savedValue: savedExporter,
        options: exporterOptions,
        isFetched,
      })
    : '';

  const persistMap = useCallback(
    (partial) => {
      const prev = (state?.selections && state.selections.map) || {};
      updateSelections({
        map: {
          importer: typeof prev.importer === 'string' ? prev.importer : '',
          exporter: typeof prev.exporter === 'string' ? prev.exporter : '',
          ...partial,
        },
      });
    },
    [state, updateSelections],
  );

  const setImporter = useCallback(
    (next) => {
      const value = (next || '').trim();
      setPartyNameParam(searchParams, setSearchParams, 'importer', value);
      persistMap({ importer: value });
    },
    [searchParams, setSearchParams, persistMap],
  );

  const setExporter = useCallback(
    (next) => {
      const value = (next || '').trim();
      setPartyNameParam(searchParams, setSearchParams, 'exporter', value);
      persistMap({ exporter: value });
    },
    [searchParams, setSearchParams, persistMap],
  );

  /** Drop exporter from the URL when leaving Shipments; keep saved preference. */
  const clearExporterUrl = useCallback(() => {
    if (!searchParams.has('exporter')) return;
    const params = new URLSearchParams(searchParams);
    params.delete('exporter');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  return {
    importer,
    exporter,
    setImporter,
    setExporter,
    clearExporterUrl,
  };
};

export default useMapPartyParams;
